import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { useState } from 'react';

import { cn } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../primitives/alert-dialog';

export type ActionModalType = 'confirm' | 'dangerous' | 'warning' | 'blocked';

interface ActionModalV2Props extends PropsWithChildren {
  variant: ActionModalType;
  title: string;
  description?: ReactNode;
  isOpen: boolean;
  isPending?: boolean;
  isConfirmDisabled?: boolean;
  confirmText?: string;
  cancelText?: string;
  onClose: () => void;
  onConfirm: (() => Promise<void>) | (() => void);
  testId?: string;
  closeOnConfirm?: boolean;
  onCloseAutoFocus?: ComponentProps<typeof AlertDialogContent>['onCloseAutoFocus'];
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 2L2 20h20L12 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ActionModalV2(props: ActionModalV2Props) {
  const {
    variant,
    title,
    description,
    confirmText,
    onClose,
    onConfirm,
    children,
    isOpen,
    isPending = false,
    isConfirmDisabled = false,
    closeOnConfirm = true,
    cancelText = 'Cancel',
    testId,
    onCloseAutoFocus,
  } = props;

  const [isConfirming, setConfirming] = useState(false);
  const isBusy = isPending || isConfirming;

  async function handleSubmit(e: React.MouseEvent) {
    e.preventDefault();
    if (isBusy || isConfirmDisabled) return;
    setConfirming(true);
    try {
      await onConfirm();
      if (closeOnConfirm) {
        onClose();
      }
    } finally {
      setConfirming(false);
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open && !isBusy) {
      onClose();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        className="w-[550px] max-w-[calc(100vw-32px)]"
        data-testid={testId}
        onCloseAutoFocus={onCloseAutoFocus}
        onEscapeKeyDown={(event) => {
          if (isBusy) event.preventDefault();
        }}
      >
        <AlertDialogHeader
          className={cn({
            'text-fire-500': ['warning', 'blocked'].includes(variant),
          })}
        >
          {['warning', 'blocked'].includes(variant) && <WarningIcon className="text-fire-500 h-6 w-6 flex-shrink-0" />}
          <AlertDialogTitle>{title}</AlertDialogTitle>
        </AlertDialogHeader>
        {description && <AlertDialogDescription className="text-base">{description}</AlertDialogDescription>}
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={testId ? `${testId}-${cancelText}-button` : undefined} disabled={isBusy}>
            {cancelText}
          </AlertDialogCancel>
          {variant !== 'blocked' && (
            <AlertDialogAction
              variant={variant === 'dangerous' ? 'dangerous' : 'primary'}
              disabled={isConfirmDisabled || isBusy}
              loading={isBusy}
              aria-busy={isBusy}
              onClick={handleSubmit}
              data-testid={testId ? `${testId}-${confirmText ?? 'confirm'}-button` : undefined}
            >
              {confirmText ?? 'Confirm'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
